import { useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { TextareaInput } from "@/components/ui/Field";
import type { BillingAnswerRow } from "@/features/timesheetFlow/billingQuestionAnswerRows";
import {
  answerTextWithoutArticles,
  formatBillingDateTime,
  formatBillingPrice,
} from "@/features/timesheetFlow/billingAnswerDisplay";
import { useCurrentUser, userCanSeePrices } from "@/lib/auth";

export type BillingAnswerArticleLine = {
  key: string;
  label: string;
  epLabel?: string | null;
  totalLabel?: string | null;
  isChild?: boolean;
};

export function BillingAnsweredRowCard({
  row,
  articleLines,
  unterpositionCount,
  unread,
  compact,
  canFollowUp,
  followUpBusy,
  onFollowUp,
  canEdit,
  onEdit,
}: {
  row: BillingAnswerRow;
  articleLines: Array<string | BillingAnswerArticleLine>;
  /** Anzahl interner Unterpositionen am Merge-Container. */
  unterpositionCount?: number;
  unread?: boolean;
  compact?: boolean;
  canFollowUp?: boolean;
  followUpBusy?: boolean;
  onFollowUp?: (row: BillingAnswerRow, text: string) => void;
  canEdit?: boolean;
  onEdit?: (row: BillingAnswerRow) => void;
}) {
  const { user } = useCurrentUser();
  const canSeePrices = userCanSeePrices(user);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUpText, setFollowUpText] = useState("");

  const answerBody = answerTextWithoutArticles(row.answerText);
  const priceLabel =
    canSeePrices && row.price != null && String(row.price).trim() !== ""
      ? formatBillingPrice(row.price)
      : null;

  const normalizedArticles: BillingAnswerArticleLine[] = articleLines.map((item, index) => {
    if (typeof item === "string") {
      return { key: `${item}-${index}`, label: item };
    }
    return item;
  });

  const submitFollowUp = () => {
    const text = followUpText.trim();
    if (!text || !onFollowUp) return;
    onFollowUp(row, text);
    setFollowUpText("");
    setFollowUpOpen(false);
  };

  return (
    <article
      className={clsx(
        "rounded-ios-lg border p-4 sm:p-5",
        unread
          ? "border-status-green/35 bg-status-green/[0.06] ring-1 ring-status-green/15"
          : "border-separator bg-card-elevated",
        compact && "p-3.5 sm:p-4",
      )}
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <h4
          className={clsx(
            "whitespace-pre-wrap font-semibold leading-snug text-label",
            compact ? "text-base" : "text-lg",
          )}
        >
          {row.questionText}
        </h4>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {unterpositionCount != null && unterpositionCount > 0 ? (
            <span className="rounded-full bg-accent/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-accent">
              {unterpositionCount} Unterpositionen
            </span>
          ) : null}
          <span
            className={clsx(
              "rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide",
              unread
                ? "bg-status-green text-white"
                : "bg-status-green/15 text-status-green",
            )}
          >
            {unread ? "Neu" : "Beantwortet"}
          </span>
        </div>
      </div>

      {row.sourceLabel ? (
        <p className="mb-2 text-xs font-medium text-label-secondary">{row.sourceLabel}</p>
      ) : null}

      {row.materialLine ? (
        <p className="mb-3 rounded-ios bg-fill-tertiary/50 px-3 py-2 text-sm font-medium text-label">
          {row.materialLine.startsWith("Pos.") ? row.materialLine : `Material: ${row.materialLine}`}
        </p>
      ) : null}

      {row.createdBy || row.createdAt || row.respondedAt ? (
        <p className="mb-3 text-xs text-label-tertiary">
          {[
            row.createdBy ? `Rückfrage von ${row.createdBy}` : null,
            row.respondedAt
              ? `Antwort ${formatBillingDateTime(row.respondedAt)}`
              : row.createdAt
                ? formatBillingDateTime(row.createdAt)
                : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      ) : null}

      {answerBody ? (
        <div
          className={clsx(
            "whitespace-pre-wrap rounded-ios bg-fill-tertiary/40 px-3 py-2.5 leading-relaxed text-label",
            compact ? "text-sm" : "text-base",
          )}
        >
          {answerBody}
        </div>
      ) : null}

      {normalizedArticles.length > 0 ? (
        <div className={clsx(answerBody ? "mt-3" : "")}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-secondary">
            {unterpositionCount != null && unterpositionCount > 0
              ? "Interne Unterpositionen"
              : "Ergänzte Artikel"}
          </p>
          <ul className="space-y-1.5">
            {normalizedArticles.map((article) => (
              <li
                key={article.key}
                className={clsx(
                  "rounded-ios border border-accent/25 bg-accent/10 px-3 py-2 text-sm font-medium leading-snug text-label",
                  article.isChild && "ml-3 border-separator/60 bg-fill-tertiary/40",
                )}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <span>{article.label}</span>
                  {canSeePrices && (article.epLabel || article.totalLabel) ? (
                    <span className="shrink-0 text-xs font-semibold text-label-secondary">
                      {[article.epLabel ? `EP ${article.epLabel}` : null, article.totalLabel]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {priceLabel ? (
        <p
          className={clsx(
            "text-sm text-label-secondary",
            answerBody || normalizedArticles.length ? "mt-3" : "",
          )}
        >
          Preis: <span className="text-base font-semibold text-label">{priceLabel}</span>
        </p>
      ) : null}

      {!answerBody && normalizedArticles.length === 0 && !priceLabel ? (
        <p className="text-sm text-label-tertiary">Keine Antwortdetails.</p>
      ) : null}

      {canEdit && onEdit ? (
        <div className="mt-4 border-t border-separator/60 pt-3">
          <Button type="button" variant="secondary" size="sm" onClick={() => onEdit(row)}>
            Antwort bearbeiten
          </Button>
        </div>
      ) : null}

      {canFollowUp ? (
        <div className="mt-4 border-t border-separator/60 pt-3">
          {!followUpOpen ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setFollowUpOpen(true)}
            >
              Erneut nachfragen
            </Button>
          ) : (
            <div className="space-y-2">
              <TextareaInput
                label="Nachfrage zur Antwort"
                rows={3}
                value={followUpText}
                onChange={(e) => setFollowUpText(e.target.value)}
                placeholder="Was möchtest du zur Antwort noch wissen?"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={followUpBusy || !followUpText.trim()}
                  onClick={submitFollowUp}
                >
                  {followUpBusy ? "Senden…" : "Nachfrage senden"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={followUpBusy}
                  onClick={() => {
                    setFollowUpOpen(false);
                    setFollowUpText("");
                  }}
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </article>
  );
}
